import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { logError } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await fetch(`${API_URL}/api/admin/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recovery_email: email }),
      });
      // Always show the same message — never leak whether the email matched.
      setSubmitted(true);
    } catch (err) {
      logError('Admin forgot-password error:', err);
      setSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <Card style={{ maxWidth: 480, width: '100%', backgroundColor: '#1e293b', border: '1px solid #334155' }} data-testid="admin-forgot-page">
        <CardHeader style={{ textAlign: 'center' }}>
          <Mail size={40} color="#f59e0b" style={{ margin: '0 auto 0.75rem' }} />
          <CardTitle style={{ color: '#f1f5f9', fontSize: '1.5rem' }}>Recover Admin Access</CardTitle>
          <p style={{ color: '#94a3b8', fontSize: '0.875rem', marginTop: '0.5rem' }}>
            Enter the recovery email configured for your account. We'll send you a reset link with your username.
          </p>
        </CardHeader>
        <CardContent>
          {submitted ? (
            <div data-testid="admin-forgot-success" style={{
              padding: '1rem',
              backgroundColor: '#064e3b',
              border: '1px solid #047857',
              borderRadius: '6px',
              color: '#a7f3d0',
              fontSize: '0.875rem',
              textAlign: 'center',
              lineHeight: 1.6,
            }}>
              If that recovery email is configured, you'll receive a reset link shortly.
              <br /><br />
              Check your inbox (and spam folder). The link expires in 1 hour.
              <Button
                onClick={() => navigate('/admin/login')}
                style={{ marginTop: '1rem', backgroundColor: '#f59e0b', color: 'white', width: '100%' }}
                data-testid="admin-forgot-back-btn"
              >
                Back to login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '1rem' }}>
              <div>
                <Label htmlFor="recovery-email" style={{ color: '#cbd5e1' }}>Recovery email</Label>
                <Input
                  id="recovery-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourdomain.com"
                  data-testid="admin-recovery-email"
                  style={{ marginTop: '0.5rem', backgroundColor: '#0f172a', border: '1px solid #334155', color: '#f1f5f9' }}
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                data-testid="admin-forgot-submit"
                style={{ backgroundColor: '#f59e0b', color: 'white', fontWeight: 600 }}
              >
                {isSubmitting ? 'Sending…' : 'Send reset link'}
              </Button>
              <button
                type="button"
                onClick={() => navigate('/admin/login')}
                style={{
                  background: 'none', border: 'none', color: '#94a3b8',
                  fontSize: '0.875rem', cursor: 'pointer', display: 'flex',
                  alignItems: 'center', gap: '0.25rem', justifyContent: 'center',
                }}
                data-testid="admin-forgot-cancel"
              >
                <ArrowLeft size={14} /> Back to login
              </button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminForgotPasswordPage;
